import styled from "styled-components";
import { transparentize } from "polished";

import { Spinner } from "_/components/spinner";
import { Button } from "_/components/button";
import { StepStatus } from "_/data/projects";
import { Icon } from "_/components/icon";
import { mobileAndTablet, mobileOnly } from "_/style/media";

export { RevisionOrJobNumber } from "_/components/jobs-listing/styled";

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding: 2rem 3rem;
  margin-right: auto;

  color: ${(p) => p.theme.text.default};

  width: 100%;
  max-width: 1500px;
  height: 100%;
`;

export const ContentSection = styled.div``;

export const ContentBlockContainer = styled.div`
  display: flex;
  gap: 1.5rem;

  ${mobileAndTablet`
    flex-direction: column;
  `}
`;

export const OptimizePanelsContainer = styled(ContentBlockContainer)`
  & > *:nth-child(1) {
    flex: 1;
  }
  & > *:nth-child(2) {
    flex: 2;
  }
  & > *:nth-child(3) {
    flex: 1;
  }

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

export const ContentBlockIcon = styled(Icon)<{ $color: string }>`
  font-size: 1.4rem;
  color: ${(p) => p.$color};
  flex-shrink: 0;
  margin-right: 0.5rem;
`;

export const ContentBlockTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 700;
`;

export const ContentBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-grow: 1;

  width: 100%;

  font-size: 0.875rem;
  padding: 2rem;
  border-radius: 12px;
  background-color: ${(p) => p.theme.background.secondary};
  border: 1px solid ${(p) => p.theme.border.default};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.02);

  p {
    color: ${(p) => p.theme.text.muted};
  }
`;

export const ContentBlockHorizontal = styled(ContentBlock)`
  align-items: center;
  text-align: center;
  font-size: 1rem;
  flex-grow: 0;
  width: fit-content;
  max-width: 50%;
  align-self: center;
`;

export const NotStartedCard = styled(ContentBlock)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-grow: 0;

  ${mobileOnly`
    flex-direction: column;
    align-items: flex-start;
  `}
`;

export const NotStartedCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
`;

export const NotStartedTitle = styled.div`
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const NotStartedDescription = styled.div`
  font-size: 0.875rem;
  color: ${(p) => p.theme.text.muted};
  line-height: 1.5;
`;

export const SectionHeaderArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  padding-bottom: 1.25rem;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 2rem;

  h2 {
    margin: 0;
    padding: 0;
    line-height: 1;
    font-size: 1rem;
    font-weight: 500;
  }
`;

export const SectionCountBadge = styled.span<{
  $success?: boolean;
  $failed?: boolean;
}>`
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
  background-color: ${(p) =>
    p.$failed
      ? transparentize(0.86, p.theme.color.palette.tomatoRed)
      : p.$success
        ? transparentize(0.86, p.theme.color.palette.kellyGreen)
        : p.theme.badge.background};
  color: ${(p) =>
    p.$failed
      ? p.theme.color.palette.tomatoRed
      : p.$success
        ? p.theme.color.palette.kellyGreen
        : p.theme.text.muted};
`;

export const SectionSubtitle = styled.div<{ $hidden?: boolean }>`
  font-size: 0.875rem;
  line-height: 1.25rem;
  min-height: 1.25rem;
  color: ${(p) => p.theme.text.muted};
  visibility: ${(p) => (p.$hidden ? "hidden" : "visible")};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const StepItem = styled.div<{
  $status: StepStatus;
  $hasSubstep?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: ${(p) => (p.$hasSubstep ? "0" : "5.25rem")};
  padding: ${(p) => (p.$hasSubstep ? "0.875rem 1rem 0.5rem" : "0.875rem 1rem")};
  background: ${(p) =>
    p.$status === "failed"
      ? p.theme.status.errorBackground
      : p.$status === "warning"
        ? p.theme.status.warningBackground
        : "transparent"};
  position: relative;

  > * {
    opacity: ${(p) =>
      p.$status === "pending" || p.$status === "skipped" ? 0.45 : 1};
  }

  ${(p) =>
    p.$hasSubstep &&
    `
    &::after {
      content: "";
      position: absolute;
      top: calc(0.875rem + 30px + 8px);
      bottom: calc(-0.5rem - 20px);
      left: calc(1rem + 10px);
      width: 1px;
      background-color: ${p.theme.color.palette.smokeGrey};
    }
  `}
`;

export const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(p) => p.theme.color.palette.smokeGrey};
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  background: rgba(255, 255, 255, 0.035);

  > * + ${StepItem} {
    border-top: 1px solid ${(p) => p.theme.color.palette.smokeGrey};
  }
`;

export const StepItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const StepItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  flex: 1;
  min-width: 0;
`;

const isActiveStatus = (status?: StepStatus) =>
  status === "completed" || status === "failed" || status === "warning";

export const StepName = styled.span<{
  $status?: StepStatus;
}>`
  font-weight: 700;
  color: ${(p) =>
    isActiveStatus(p.$status)
      ? p.theme.text.default
      : p.theme.color.palette.fogGrey};
`;

export const StepSubtitle = styled.span<{ $status: StepStatus }>`
  font-size: 0.75rem;
  color: ${(p) =>
    p.$status === "failed"
      ? transparentize(0.2, p.theme.color.palette.tomatoRed)
      : p.$status === "warning"
        ? transparentize(0.2, p.theme.color.palette.sunsetOrange)
        : p.theme.text.muted};
  font-weight: 400;
`;

export const TimeSavedSubtitle = styled.span`
  font-size: 0.75rem;
  color: ${(p) => p.theme.text.muted};
  font-weight: 400;
`;

export const TimeSavedValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${(p) => p.theme.text.muted};
  margin-right: 0.25rem;
`;

export const StepStatusText = styled.span`
  font-size: 0.875rem;
  color: ${(p) => p.theme.text.muted};
  white-space: nowrap;
`;

export const StepDivider = styled.hr<{ $status?: StepStatus }>`
  border: none;
  border-top: 1px solid
    ${(p) =>
      transparentize(
        0.7,
        p.$status === "warning"
          ? p.theme.color.palette.sunsetOrange
          : p.theme.color.palette.tomatoRed
      )};
  margin: 0.625rem 0 0;
`;

export const StepFailedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.625rem 0 0;
`;

export const StepLearnMoreLink = styled.a<{ $status?: StepStatus }>`
  font-size: 0.75rem;
  color: ${(p) =>
    p.$status === "warning"
      ? p.theme.color.palette.sunsetOrange
      : p.theme.color.palette.tomatoRed};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export const StepCompleteText = styled.span`
  font-size: 0.875rem;
  font-weight: 700;
  color: ${(p) => p.theme.text.default};
  white-space: nowrap;
`;

export const StepPercentage = styled.span`
  font-size: 0.875rem;
  color: ${(p) => p.theme.color.palette.cornflowerBlue};
  white-space: nowrap;
`;

export const StepInlineProgress = styled.progress`
  width: 100%;
  height: 3px;
  border: none;
  border-radius: 999px;
  display: block;
  margin-top: 0.5rem;
  background-color: ${(p) => p.theme.progressBar.background};

  &::-webkit-progress-bar {
    background-color: ${(p) => p.theme.progressBar.background};
    border-radius: 999px;
  }
  &::-webkit-progress-value {
    background-color: ${(p) => p.theme.progressBar.progress.info};
    border-radius: 999px;
  }
  &::-moz-progress-bar {
    background-color: ${(p) => p.theme.progressBar.progress.info};
    border-radius: 999px;
  }
`;

export const RunningSpinner = Spinner;

export const PendingDot = styled.div`
  width: 20px;
  height: 20px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid ${(p) => p.theme.color.palette.smokeGrey};
  background-color: ${(p) => p.theme.color.palette.smokeGrey}33;
  flex-shrink: 0;
`;

export const QueuedStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;
`;

export const QueuedSpinner = styled(Spinner).attrs({ size: 32 })``;

export const QueuedTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const QueuedTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

export const QueuedTitle = styled.span`
  font-size: 1rem;
`;

export const QueuedMessage = styled.div`
  font-size: 0.875rem;
  color: ${(p) => p.theme.text.muted};
  max-width: 32rem;
`;

export const ChecksFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-radius: 4px;
  border: 1px solid ${(p) => p.theme.color.palette.cornflowerBlue};
  color: ${(p) => p.theme.color.palette.cornflowerBlue};
  font-size: 0.875rem;
`;

export const PanelFooter = styled.div`
  margin-top: auto;
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;

  /* Alert has margin-bottom built in; remove it at the card edge */
  > * {
    margin-bottom: 0;
  }
`;

export const StepIcon = styled(Icon)<{
  $status: StepStatus;
}>`
  font-size: 20px;
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  color: ${(p) =>
    ({
      failed: p.theme.color.palette.tomatoRed,
      running: p.theme.color.palette.cornflowerBlue,
      completed: p.theme.color.palette.kellyGreen,
      skipped: p.theme.color.palette.kellyGreen,
      pending: p.theme.color.palette.smokeGrey,
      warning: p.theme.color.palette.sunsetOrange,
    })[p.$status]};
`;

export const LayerControlContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  height: 100%;
  width: max-content;

  padding: 1rem;

  border-radius: 5px;

  background-color: ${(p) => p.theme.background.secondary};
`;

export const PointCloudControlContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  width: 100%;
  height: max-content;

  padding: 1rem;
  border-radius: 5px;

  background-color: ${(p) => p.theme.background.secondary};
`;

export const ControlsBoxSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  &:not(:first-child) {
    padding-top: 0.625rem;
  }
`;

export const ControlsBoxTitle = styled.div`
  display: flex;
  gap: 0.5rem;
`;

export const ControlsBox = styled.div`
  font-size: 0.875rem;
  font-weight: 600;

  color: ${(p) => p.theme.text.muted};

  padding: 1rem;

  display: flex;
  flex-direction: column;

  gap: 1rem;
`;

export const RadioOptions = styled.div`
  color: ${(p) => p.theme.text.default};
  font-weight: 400;
  margin-top: 1rem;
  margin-bottom: 3rem;

  /* More breathing room between radio options */
  & > div > *:not(:last-of-type) {
    margin-bottom: 1.5rem;
  }
`;

export const CreateRevisionButton = styled(Button)`
  width: 100%;
  margin-top: 0.25rem;
`;

export const OptimizationSubstepTitle = styled.span`
  font-weight: 700;
  color: ${(p) => p.theme.text.muted};
`;

export const OptimizationSubstepTitleSmall = styled.span`
  font-size: 0.75rem;
  font-weight: 400;
  color: ${(p) => p.theme.text.muted};
  margin-left: 0.25rem;
`;

export const OptimizationLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

export const OptimizationLabelText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
`;

export const NextStepsContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  flex: 1;
`;

export const NextStepsItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

export const NextStepsLabel = styled.div<{
  $muted?: boolean;
  $compact?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${(p) => p.theme.text.muted};
  margin-bottom: ${(p) => (p.$compact ? "0.25rem" : "0.5rem")};
  opacity: ${(p) => (p.$muted ? 0.4 : 1)};
`;

export const NextStepsDivider = styled.hr`
  border: none;
  border-top: 1px solid ${(p) => p.theme.color.palette.smokeGrey};
  margin: 0.5rem 0 0.75rem;
`;

export const NextStepsLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  font-size: 0.875rem;
  padding-left: 0.75rem;
`;

export const LayerSliderContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
`;

export const ControlLabel = styled.span`
  font-size: 0.875rem;
  color: ${(p) => p.theme.text.muted};
  font-weight: 600;
`;

export const RightSideContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const ProgressWrapper = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const SimErrorsContainer = styled.div`
  margin-top: 1rem;
`;

export const NotStartedMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
`;

export const AlertRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

export const OptimizationSection = styled.div`
  padding: 0.5rem 1rem 0.875rem 3.5rem;
  min-height: 3.75rem;
  display: flex;
  align-items: center;

  > * {
    flex: 1;
  }
`;

/* Wrapper around the child icon — hosts both L-connector segments dynamically */
export const OptimizationIconWrapper = styled.div`
  --connector-gap: 8px;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  height: 20px;

  /* horizontal segment: ends --connector-gap before icon left edge */
  &::before {
    content: "";
    position: absolute;
    top: 50%;
    right: calc(100% + var(--connector-gap));
    width: calc(2.5rem - 18px);
    height: 1px;
    background-color: ${(p) => p.theme.color.palette.smokeGrey};
  }
`;
